/**
 * Tag helpers for the consumer site.
 * PMS Admin stores `Property.tags` as comma-separated Tag **IDs** (see property.component onSubmit).
 * Matching must use those IDs plus optional master tag rows ({ id, name }) from `/tag/get`.
 */

/**
 * Lowercase alphanumeric-only key (handles "Pool Villas", "POOL_VILLAS", "poolvillas").
 */
export function normalizeTagCompact(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

export function parseStoredPropertyTagValues(rawTags) {
  if (rawTags == null || rawTags === '') return [];

  let values = [];
  if (Array.isArray(rawTags)) {
    values = rawTags;
  } else {
    const stringValue = String(rawTags).trim();
    if (!stringValue) return [];

    try {
      const parsed = JSON.parse(stringValue);
      values = Array.isArray(parsed) ? parsed : stringValue.split(',');
    } catch {
      values = stringValue.split(',');
    }
  }

  return Array.from(
    new Set(
      values
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .map((value) => String(value).replace(/^["']|["']$/g, '').trim().toLowerCase())
        .filter(Boolean),
    ),
  );
}

/** Tag IDs only (as stored on Property.tags). */
export function parseStoredPropertyTagIds(rawTags) {
  return parseStoredPropertyTagValues(rawTags).filter((v) => /^\d+$/.test(v));
}

/**
 * Resolve a URL / UI token or tag name to a Tag id string using master list from API.
 */
export function resolveTagIdFromCatalog(token, tags = []) {
  if (token == null || token === '') return null;
  const t = String(token).trim();
  if (/^\d+$/.test(t)) return t;
  const want = normalizeTagCompact(t);
  if (!want) return null;
  for (const row of tags || []) {
    const id = row?.id;
    const name = row?.name;
    if (id != null && String(id) === t) return String(id);
    if (name != null && normalizeTagCompact(name) === want) return String(id);
  }
  return null;
}

/**
 * True if property has the tag: compares stored **IDs** to catalog, and optional alias strings.
 */
export function propertyHasTag(property, tokenOrSpec, tagsCatalog = []) {
  if (tokenOrSpec == null || tokenOrSpec === '') return true;

  const token =
    typeof tokenOrSpec === 'object' && tokenOrSpec !== null
      ? tokenOrSpec.primary || tokenOrSpec.token || ''
      : tokenOrSpec;
  const extraAliases =
    typeof tokenOrSpec === 'object' && tokenOrSpec !== null && Array.isArray(tokenOrSpec.matchNames)
      ? tokenOrSpec.matchNames
      : [];

  if (!token && !extraAliases.length) return true;

  const idsOnProperty = parseStoredPropertyTagIds(property?.tags);
  const candidates = [token, ...extraAliases].filter(Boolean);

  for (const c of candidates) {
    const id = resolveTagIdFromCatalog(c, tagsCatalog);
    if (id && idsOnProperty.includes(String(id))) return true;
  }

  const compactNeedles = new Set(candidates.map((c) => normalizeTagCompact(c)).filter(Boolean));
  for (const id of idsOnProperty) {
    const row = (tagsCatalog || []).find((r) => String(r?.id) === String(id));
    if (row?.name && compactNeedles.has(normalizeTagCompact(row.name))) return true;
  }

  const storedValues = parseStoredPropertyTagValues(property?.tags);
  for (const needle of compactNeedles) {
    if (storedValues.some((v) => normalizeTagCompact(v) === needle)) return true;
  }

  return false;
}
